import React from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { contactForm } from "@/utils/content";
import InputGroup from "../common/input-group";
import { useIsMobile } from "@/hooks/useIsMobile";

type Inputs = {
  username: string;
  email: string;
  phone: string;
  companyName: string;
  accountNo: string;
  additionalDetails: string;
  selectedBusinessType: string;
  selectedMonthlyVolume: string;
  selectedHelpSubject: string;
  otherCarriers: string;
  carriers: Array<boolean>;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  username: yup.string().required("Full Name is required"),
  phone: yup.string().required("Phone is required"),
});

let businessTypeData = [
  "Seller/merchant",
  "eCommerce Platform",
  "Fulfillment",
  "Marketplace",
  "Logistics Provider (WMS, IMS, etc)",
];
let monthlyShippingVolume = ["0-999", "1000-4999", "5000-9999", "10000+"];
let helpSubject = [
  "API",
  "Getting Started",
  "Rates and Pricing",
  "Store and Platform Integrations",
  "Errors with Website",
  "Other",
];

const carrierOptions = [
  {
    label: "Canada Post",
  },
  {
    label: "UPS",
  },
  {
    label: "FedEx",
  },
  {
    label: "DHL",
  },
  {
    label: "USPS",
  },
  {
    label: "CANPAR",
  },
  {
    label: "Other",
    textAvailable: true,
  },
];

type Props = {
  submitHandler: (data: any) => Promise<void>;
  loading: boolean;
};

const ContactForm = ({ submitHandler, loading }: Props) => {
  const content = contactForm.en;
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema) as any,
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit = (data: any) => {
    reset();
    submitHandler(data);
  };

  return (
    <>
      <Card className="shadow">
        <CardBody className={isMobile ? "p-3" : "p-5"}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputGroup
              required
              label={content.name}
              name="username"
              register={register}
              error={errors.username?.message}
            />
            <InputGroup
              label={content.companyName}
              name="companyName"
              register={register}
              error={errors.companyName?.message}
            />
            <InputGroup
              required
              label={content.email}
              name="email"
              register={register}
              error={errors.email?.message}
            />
            <InputGroup
              required
              label={content.phone}
              name="phone"
              register={register}
              error={errors.phone?.message}
            />
            <FormGroup>
              <Label for="selectedBusinessType">{content.businessType}</Label>
              <select
                className={classnames("form-control", {
                  "is-invalid": errors.selectedBusinessType?.message,
                })}
                {...register("selectedBusinessType", { required: true })}
              >
                <option value="">-- Please Select --</option>
                {businessTypeData.map((bt, i) => (
                  <option key={i} value={bt}>
                    {bt}
                  </option>
                ))}
              </select>
              {errors.selectedBusinessType && (
                <div className="invalid-feedback">
                  {errors.selectedBusinessType?.message}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="selectedMonthlyVolume">
                {content.shippingVolume}
              </Label>
              <select
                className="form-control"
                {...register("selectedMonthlyVolume", { required: true })}
              >
                <option value="">-- Please Select --</option>
                {monthlyShippingVolume.map((bt, i) => (
                  <option key={i} value={bt}>
                    {bt}
                  </option>
                ))}
              </select>
            </FormGroup>
            <FormGroup>
              <Label for="selectedHelpSubject">{content.helpSubject}</Label>
              <select
                className={classnames("form-control", {
                  "is-invalid": errors.selectedHelpSubject?.message,
                })}
                {...register("selectedHelpSubject", { required: true })}
              >
                <option value="">-- Please Select --</option>
                {helpSubject.map((bt, i) => (
                  <option key={i} value={bt}>
                    {bt}
                  </option>
                ))}
              </select>
              {errors.selectedHelpSubject && (
                <div className="invalid-feedback">
                  {errors.selectedHelpSubject?.message}
                </div>
              )}
            </FormGroup>
            <InputGroup
              label={content.accountNo}
              name="accountNo"
              register={register}
              error={errors.accountNo?.message}
            />
            <FormGroup>
              <Label for="additionalDetails">{content.carrierName}</Label>
              <Row>
                {carrierOptions.map((c, i) => (
                  <>
                    <Col key={c.label} xs={6} md={4} lg={3}>
                      <label htmlFor={`carriers${i}`} className="my-3">
                        <input
                          id={`carriers${i}`}
                          type="checkbox"
                          className="me-2"
                          value={c.label}
                          {...register(`carriers.${i}`)}
                        />
                        {c.label}
                      </label>
                    </Col>
                    {c.textAvailable && (
                      <Col xs={12} md={4} lg={3}>
                        <input
                          className="form-control"
                          disabled={!watch(`carriers.${i}`)}
                          placeholder="What (other) carriers are you using?"
                          {...register("otherCarriers", {
                            required: true,
                            maxLength: 50,
                          })}
                        />
                      </Col>
                    )}
                  </>
                ))}
              </Row>
            </FormGroup>
            <FormGroup>
              <Label for="additionalDetails">{content.additionalDetails}</Label>
              <textarea
                className="form-control"
                {...register("additionalDetails", { required: true })}
              />
            </FormGroup>
            <div className="text-center">
              <Button
                className="btn-green fw-bold my-4"
                disabled={loading}
                type="submit"
              >
                {content.btnConnect}
                {loading && <Spinner size="sm" className="ms-3" />}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  );
};

export default ContactForm;
